import React, { useState, useEffect } from 'react';
import StepTShirtSize from './StepTShirtSize';
import StepHeight from './StepHeight';
import StepWeight from './StepWeight';
import StepWaist from './StepWaist';
import StepBellySize from './StepBellySize';
import StepChestShoulders from './StepChestShoulders';
import StepCollarSize from './StepCollarSize';
import StepSleeveLength from './StepSleeveLength';
import StepTuckPreference from './StepTuckPreference';
import StepFitPreference from './StepFitPreference';
import StepReviewSubmit from './StepReviewSubmit';
import Header from '../Header';
import Footer from '../Footer';
import { API_BASE_URL } from '../../config/apiConfig';

const SESSION_SIZE_KEY = 'ccc_guest_size';
const SESSION_RETURN_KEY = 'ccc_return_to';

const safeParse = (raw, fallback) => {
  try { return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};

const setGuestSize = (obj) => {
  try {
    sessionStorage.setItem(SESSION_SIZE_KEY, JSON.stringify(obj || null));
  } catch {}
  window.dispatchEvent(new Event('ccc_guest_size_updated'));
};

const getReturnTo = () => sessionStorage.getItem(SESSION_RETURN_KEY) || '/';
const clearReturnTo = () => { try { sessionStorage.removeItem(SESSION_RETURN_KEY); } catch {} };


const SmartSizeWizard = ({ userId, initialData }) => {
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  // normalize initial data (handles both camelCase and PascalCase keys)
  const [formData, setFormData] = useState(() => {
    if (!initialData) {
      return {
        smartSizeId: null,
        name: '',
        tShirtSize: '',
        height: '',
        weight: '',
        waist: '',
        bellySize: '',
        chestShoulders: '',
        collarSize: '',
        sleeveLength: '',
        tuckPreference: '',
        fitPreference: '',
      };
    }

    return {
      smartSizeId: initialData.smartSizeId ?? initialData.SmartSizeId ?? null,
      name: initialData.name ?? initialData.Name ?? '',
      tShirtSize: initialData.tShirtSize ?? initialData.TShirtSize ?? '',
      height: initialData.height ?? initialData.Height ?? '',
      weight: initialData.weight ?? initialData.Weight ?? '',
      waist: initialData.waist ?? initialData.Waist ?? '',
      bellySize: initialData.bellySize ?? initialData.BellySize ?? '',
      chestShoulders: initialData.chestShoulders ?? initialData.ChestShoulders ?? '',
      collarSize: initialData.collarSize ?? initialData.CollarSize ?? '',
      sleeveLength: initialData.sleeveLength ?? initialData.SleeveLength ?? '',
      tuckPreference: initialData.tuckPreference ?? initialData.TuckPreference ?? '',
      fitPreference: initialData.fitPreference ?? initialData.FitPreference ?? '',
    };
  });

  useEffect(() => {
    return () => {
      localStorage.removeItem('editSmartSize');
    };
  }, []);

  const next = () => setStep((prev) => Math.min(prev + 1, steps.length - 1));
  const back = () => setStep((prev) => Math.max(prev - 1, 0));

  const handleSave = async () => {
  if (!formData.name) {
    alert('Please enter a name for this SmartSize profile.');
    return;
  }

  const token = localStorage.getItem('token');

  // ✅ NOT LOGGED IN => save to sessionStorage and go back
if (!token) {
  const guestPayload = {
    sizeType: 'smart',
    sizeId: 'guest_smart',          // ✅ ADD THIS (critical)
    smartSizeName: formData.name,   // ok to keep (optional)

    // keep full data so you can prefill later if needed
    smartSizeData: {
      name: formData.name || '',
      tShirtSize: formData.tShirtSize || '',
      height: formData.height || '',
      weight: formData.weight || '',
      waist: formData.waist || '',
      bellySize: formData.bellySize || '',
      chestShoulders: formData.chestShoulders || '',
      collarSize: formData.collarSize || '',
      sleeveLength: formData.sleeveLength || '',
      tuckPreference: formData.tuckPreference || '',
      fitPreference: formData.fitPreference || '',
    },
  };

  setGuestSize(guestPayload);

  alert('Smart Size saved for this browser session.');
  localStorage.removeItem('editSmartSize');

  const backTo = getReturnTo();
  clearReturnTo();
  window.location.href = backTo;
  return;
}


  // ✅ LOGGED IN => save to DB like before
  setSaving(true);

  const isEdit = !!formData.smartSizeId;
  const url = `${API_BASE_URL}/UserManagement/${isEdit ? 'UpdateSmartSize' : 'AddSmartSize'}`;

  const currentUser = (() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch { return null; }
  })();

  const effectiveUserId = userId || currentUser?.userId || null;

  const payload = {
    smartSizeId: formData.smartSizeId || undefined,
    userId: effectiveUserId,
    name: formData.name || '',
    tShirtSize: formData.tShirtSize || null,
    height: formData.height || null,
    weight: formData.weight || null,
    waist: formData.waist || null,
    bellySize: formData.bellySize || null,
    chestShoulders: formData.chestShoulders || null,
    collarSize: formData.collarSize || null,
    sleeveLength: formData.sleeveLength || null,
    tuckPreference: formData.tuckPreference || null,
    fitPreference: formData.fitPreference || null,
  };

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      let err;
      try { err = await res.json(); } catch { err = null; }
      alert(err?.message || 'Save failed');
      return;
    }

    alert('Smart Size saved successfully');
    localStorage.removeItem('editSmartSize');

    const backTo = getReturnTo();
    clearReturnTo();

    // refresh listeners (ClientDetails / Customize size panels etc.)
    window.dispatchEvent(new Event('ccc_user_updated'));

    window.location.href = backTo;
  } catch (err) {
    alert('Something went wrong. Please try again.');
  } finally {
    setSaving(false);
  }
};


  const steps = [
    <StepTShirtSize data={formData} setData={setFormData} onNext={next} />,
    <StepHeight data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepWeight data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepWaist data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepBellySize data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepChestShoulders data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepCollarSize data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepSleeveLength data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepTuckPreference data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepFitPreference data={formData} setData={setFormData} onNext={next} onPrev={back} />,
    <StepReviewSubmit
      data={formData}
      setData={setFormData}
      onPrev={back}
      onSave={handleSave}
      saving={saving}
    />,
  ];

  return (
    <>
      <Header />
      <div className="wizard-container">
        {steps[step]}
      </div>
      <Footer />
    </>
  );
};

export default SmartSizeWizard;
